export type PresetCategory = "social" | "reel" | "print";

export interface FormatPreset {
  id: string;
  label: string;
  category: PresetCategory;
  kind: "design" | "video";
  width: number;
  height: number;
  /** Physical size for print formats. `width`/`height` are then CSS pixels (96 dpi). */
  print?: { widthMm: number; heightMm: number; bleedMm: number; safeMm: number; provider?: string };
}

export const CSS_DPI = 96;
export const MM_PER_INCH = 25.4;

export function mmToPx(mm: number): number {
  return Math.round((mm / MM_PER_INCH) * CSS_DPI);
}

function printPreset(id: string, label: string, widthMm: number, heightMm: number): FormatPreset {
  return {
    id,
    label,
    category: "print",
    kind: "design",
    width: mmToPx(widthMm),
    height: mmToPx(heightMm),
    // Defaults of the default print shop (SAXOPRINT); changeable per document.
    print: { widthMm, heightMm, bleedMm: 2, safeMm: 3, provider: "saxoprint" },
  };
}

export const FORMAT_PRESETS: readonly FormatPreset[] = [
  {
    id: "instagram-portrait",
    label: "Instagram Post (4:5)",
    category: "social",
    kind: "design",
    width: 1080,
    height: 1350,
  },
  {
    id: "square",
    label: "Post quadratisch (1:1)",
    category: "social",
    kind: "design",
    width: 1080,
    height: 1080,
  },
  {
    id: "story",
    label: "Story (9:16)",
    category: "social",
    kind: "design",
    width: 1080,
    height: 1920,
  },
  {
    id: "linkedin-landscape",
    label: "LinkedIn Querformat",
    category: "social",
    kind: "design",
    width: 1200,
    height: 627,
  },
  {
    id: "presentation",
    label: "Präsentation (16:9)",
    category: "social",
    kind: "design",
    width: 1920,
    height: 1080,
  },
  // App Store Connect: screenshots for 6.9″ iPhones (portrait).
  {
    id: "app-store",
    label: "App-Store-Screenshot (iPhone 6,9″)",
    category: "social",
    kind: "design",
    width: 1320,
    height: 2868,
  },
  {
    id: "facebook-post",
    label: "Facebook-Beitrag",
    category: "social",
    kind: "design",
    width: 1200,
    height: 630,
  },
  {
    id: "x-post",
    label: "X / Twitter-Beitrag",
    category: "social",
    kind: "design",
    width: 1600,
    height: 900,
  },
  {
    id: "youtube-thumbnail",
    label: "YouTube-Thumbnail",
    category: "social",
    kind: "design",
    width: 1280,
    height: 720,
  },
  // Video formats: the shorter side is 1080 px (export resolutions refer to it).
  {
    id: "reel",
    label: "Reel / TikTok / Shorts",
    category: "reel",
    kind: "video",
    width: 1080,
    height: 1920,
  },
  {
    id: "video-feed",
    label: "Feed-Video hochkant",
    category: "reel",
    kind: "video",
    width: 1080,
    height: 1350,
  },
  {
    id: "video-square",
    label: "Quadrat",
    category: "reel",
    kind: "video",
    width: 1080,
    height: 1080,
  },
  {
    id: "video-landscape",
    label: "Querformat (YouTube, LinkedIn, Website)",
    category: "reel",
    kind: "video",
    width: 1920,
    height: 1080,
  },
  printPreset("a6", "Flyer A6", 105, 148),
  printPreset("a5", "Flyer A5", 148, 210),
  printPreset("a4", "A4", 210, 297),
  printPreset("a3", "Plakat A3", 297, 420),
  printPreset("a2", "Plakat A2", 420, 594),
  printPreset("a1", "Plakat A1", 594, 841),
  printPreset("din-lang", "Flyer DIN lang", 99, 210),
  printPreset("business-card", "Visitenkarte", 85, 55),
];

/** Smallest and largest side of a custom design size in pixels. */
export const CUSTOM_SIZE_MIN = 50;
export const CUSTOM_SIZE_MAX = 10000;

/** A design format of any size („Eigene Größe“, e.g. for transfers). */
export function customPreset(width: number, height: number): FormatPreset {
  const w = Math.round(Math.max(CUSTOM_SIZE_MIN, Math.min(CUSTOM_SIZE_MAX, width)));
  const h = Math.round(Math.max(CUSTOM_SIZE_MIN, Math.min(CUSTOM_SIZE_MAX, height)));
  return {
    id: "custom",
    label: `${w} × ${h} px`,
    category: "social",
    kind: "design",
    width: w,
    height: h,
  };
}

export const PRESET_CATEGORY_LABELS: Record<PresetCategory, string> = {
  social: "Social Media",
  reel: "Video",
  print: "Druck",
};

export function getPreset(id: string): FormatPreset | undefined {
  return FORMAT_PRESETS.find((preset) => preset.id === id);
}

/** Formats of video documents (9:16, 4:5, 1:1, 16:9). */
export const VIDEO_PRESETS: readonly FormatPreset[] = FORMAT_PRESETS.filter(
  (preset) => preset.kind === "video",
);

/** Aspect ratio as a label, e.g. "9:16" (reduced, or rounded to two decimals). */
export function aspectLabel(width: number, height: number): string {
  const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));
  const w = Math.round(width);
  const h = Math.round(height);
  const d = gcd(w, h) || 1;
  if (w / d <= 32 && h / d <= 32) return `${w / d}:${h / d}`;
  return (w / h).toLocaleString("de-DE", { maximumFractionDigits: 2 });
}
